import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "next-themes";
import {
  Sun,
  Moon,
  LogOut,
  ChevronDown,
  Zap,
  Crown,
  CreditCard,
  Sparkles,
  UserCog,
  Shield,
  Menu,
  X,
  Bell,
  ScanEye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import logoLight from "@/assets/logo-light.svg";
import logoDark from "@/assets/logo-dark.svg";

const PLAN_ICON = { starter: Sparkles, pro: Zap, business: Crown };
const PLAN_COLOR = {
  free: "text-muted-foreground",
  starter: "text-emerald-400",
  pro: "text-brand-primary",
  business: "text-purple-400",
};
const DAILY_CREDIT_LIMITS = {
  free: 1,
  starter: 5,
  pro: 15,
  business: 40,
  unlimited: 100,
};
const AUTH_MINIMAL_ROUTES = new Set([
  "/login",
  "/register",
  "/forgot-password",
  "/verify-email",
  "/resend-verification",
  "/auth/callback",
]);

export default function Header() {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { permission, requestPermission } = usePushNotifications(user);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const menuRef = useRef(null);

  const isMinimalHeader = AUTH_MINIMAL_ROUTES.has(location.pathname);
  const currentTheme = resolvedTheme || theme || "dark";

  const appLinks = useMemo(
    () => [
      { to: "/dashboard", label: t("nav.dashboard") },
      { to: "/analyze", label: t("nav.analyze") },
      { to: "/history", label: t("nav.history") },
    ],
    [t],
  );

  // Show push notification prompt ONCE — if user dismisses, never show again
  useEffect(() => {
    if (user && permission === "default") {
      const dismissed = localStorage.getItem("veriksa_push_dismissed");
      if (!dismissed) {
        const timer = setTimeout(() => setShowPushPrompt(true), 5000);
        return () => clearTimeout(timer);
      }
    }
  }, [user, permission]);

  useEffect(() => {
    setMenuOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const handleLogout = async () => {
    setMenuOpen(false);
    setMobileOpen(false);
    await logout();
    toast.success(t("nav.signOut"));
    const landingUrl =
      process.env.REACT_APP_WEB_URL || "https://localhost.veriksa.dev";
    window.location.assign(landingUrl);
  };

  const plan = String(user?.plan || "free").toLowerCase();
  const PlanIcon = PLAN_ICON[plan];
  const isAdmin = user?.role === "admin";
  const dailyLimit = Number(
    user?.daily_credit_limit ??
      user?.daily_budget_limit ??
      DAILY_CREDIT_LIMITS[plan] ??
      1
  );
  const creditsUsedToday = Math.max(0, Number(user?.credits_used_today || 0));
  const creditsRemainingToday = Math.max(0, dailyLimit - creditsUsedToday);
  const usagePct =
    dailyLimit > 0
      ? Math.min(100, Math.round((creditsUsedToday / dailyLimit) * 100))
      : 0;
  const usageColor =
    usagePct >= 100
      ? "bg-red-500"
      : usagePct >= 80
        ? "bg-amber-500"
        : "bg-brand-primary";

  const isActive = (path) => location.pathname.startsWith(path);

  const initials = (user?.name || user?.email || "U")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <>
      <header
        className="relative z-50 h-[60px] border-b border-border bg-background/95 backdrop-blur-md"
        data-testid="header"
      >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-full flex items-center gap-6">
        {/* Logo */}
        <Link
          to="/dashboard"
          className="flex items-center flex-shrink-0"
          data-testid="logo-link"
        >
          <img
            src={resolvedTheme === "dark" ? logoDark : logoLight}
            alt="Veriksa"
            className="h-8 w-auto object-contain"
          />
        </Link>

        {isMinimalHeader ? <div className="flex-1" /> : null}

        {!isMinimalHeader ? (
          <>
            {/* Top Nav Links (desktop) */}
            <nav
              className="hidden md:flex items-center gap-1 flex-1"
              data-testid="top-nav"
            >
              {appLinks.map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  className={`px-3 py-1.5 rounded-sm text-sm font-medium transition-colors ${
                    isActive(to)
                      ? "text-foreground bg-secondary"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                  }`}
                  data-testid={`nav-${to.replace("/", "")}`}
                >
                  {label}
                </Link>
              ))}
            </nav>

            {/* Mobile hamburger */}
            <button
              className="md:hidden ml-auto p-1.5 rounded-sm hover:bg-secondary/60 transition-colors"
              onClick={() => setMobileOpen((prev) => !prev)}
              data-testid="mobile-menu-btn"
              aria-label="Open app menu"
            >
              {mobileOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </button>

            {/* Right Controls (desktop) */}
            <div className="hidden md:flex items-center gap-2 ml-auto">
              {/* Usage Indicator */}
              {user ? (
                <div
                  className="hidden md:flex items-center gap-2 px-2.5 py-1 rounded-sm border border-border bg-secondary/30"
                  data-testid="usage-indicator"
                  title="Kredit tersisa hari ini"
                >
                  <div className="w-16 h-1 rounded-full bg-secondary">
                    <div
                      className={`h-full rounded-full transition-all ${usageColor}`}
                      style={{ width: `${usagePct}%` }}
                    />
                  </div>
                  <span
                    className="text-xs font-mono text-muted-foreground"
                    data-testid="usage-counter"
                  >
                    {creditsRemainingToday}/{dailyLimit}
                  </span>
                </div>
              ) : null}

              {/* Notification shortcut */}
              <Link to="/profile#notifications" data-testid="notifications-link">
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-sm h-7 w-7"
                  aria-label="Notifications"
                >
                  <Bell className="w-3.5 h-3.5" />
                </Button>
              </Link>

              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() =>
                  setTheme(currentTheme === "dark" ? "light" : "dark")
                }
                data-testid="theme-toggle"
                className="rounded-sm h-7 w-7"
              >
                {currentTheme === "dark" ? (
                  <Sun className="w-3.5 h-3.5" />
                ) : (
                  <Moon className="w-3.5 h-3.5" />
                )}
              </Button>

              {/* Authenticated: Avatar Dropdown */}
              {user ? (
                <div className="relative" ref={menuRef}>
                  <button
                    onClick={() => setMenuOpen((prev) => !prev)}
                    className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-sm hover:bg-secondary/60 transition-colors"
                    data-testid="user-menu-trigger"
                  >
                    {user.picture ? (
                      <img
                        src={user.picture}
                        alt={user.name || "User Avatar"}
                        className="w-6 h-6 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-brand-primary/20 flex items-center justify-center">
                        <span className="font-heading font-bold text-[10px] text-brand-primary">
                          {initials}
                        </span>
                      </div>
                    )}
                    <span className="hidden sm:block text-sm font-medium max-w-[100px] truncate">
                      {user.name?.split(" ")[0]}
                    </span>
                    {PlanIcon ? (
                      <PlanIcon className={`w-3 h-3 ${PLAN_COLOR[plan]}`} />
                    ) : null}
                    <ChevronDown className="w-3 h-3 text-muted-foreground" />
                  </button>

                  {menuOpen ? (
                    <div
                      className="absolute right-0 top-full mt-1 w-52 rounded-md border border-border bg-background shadow-xl py-1 z-50"
                      data-testid="user-menu"
                    >
                      <div className="px-4 py-2.5 border-b border-border">
                        <p className="text-sm font-medium truncate">{user.name}</p>
                        <p className="text-xs font-mono text-muted-foreground truncate">
                          {user.email}
                        </p>
                        <span
                          className={`text-xs font-mono mt-1 inline-block ${PLAN_COLOR[plan]}`}
                        >
                          {plan.charAt(0).toUpperCase() + plan.slice(1)} Plan
                        </span>
                      </div>
                      <div className="py-1">
                        <Link
                          to="/profile"
                          onClick={() => setMenuOpen(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-secondary/60 transition-colors"
                          data-testid="menu-profile"
                        >
                          <UserCog className="w-3.5 h-3.5 text-muted-foreground" />{" "}
                          {t("nav.editProfile")}
                        </Link>
                        <Link
                          to="/subscription"
                          onClick={() => setMenuOpen(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-secondary/60 transition-colors"
                          data-testid="menu-subscription"
                        >
                          <CreditCard className="w-3.5 h-3.5 text-muted-foreground" />{" "}
                          {t("nav.subscription")}
                        </Link>
                        {isAdmin ? (
                          <Link
                            to="/admin"
                            onClick={() => setMenuOpen(false)}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-brand-primary hover:bg-brand-primary/10 transition-colors"
                            data-testid="menu-admin"
                          >
                            <Shield className="w-3.5 h-3.5" /> {t("nav.admin")}
                          </Link>
                        ) : null}
                      </div>
                      <div className="border-t border-border py-1">
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                          data-testid="logout-btn"
                        >
                          <LogOut className="w-3.5 h-3.5" /> {t("nav.signOut")}
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </>
        ) : null}
      </div>

      {/* Mobile Drawer */}
      {!isMinimalHeader && mobileOpen ? (
        <div
          className="md:hidden absolute top-[60px] left-0 right-0 border-b border-border bg-background shadow-lg z-40"
          data-testid="mobile-menu"
        >
          <nav className="flex flex-col p-3 gap-1">
            {appLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                onClick={() => setMobileOpen(false)}
                className={`px-4 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                  isActive(to)
                    ? "text-foreground bg-secondary"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                }`}
                data-testid={`mobile-nav-${to.replace("/", "")}`}
              >
                {label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2 px-4 pb-3 pt-1 border-t border-border">
            {user ? (
              <div
                className="flex items-center gap-2 px-2.5 py-1 rounded-sm border border-border bg-secondary/30"
                data-testid="mobile-usage"
              >
                <span className="text-xs font-mono text-muted-foreground">
                  {creditsRemainingToday}/{dailyLimit}
                </span>
              </div>
            ) : null}
            <Link
              to="/profile#notifications"
              onClick={() => setMobileOpen(false)}
              className="text-sm text-muted-foreground hover:text-foreground"
              aria-label="Notifications"
            >
              <Bell className="w-4 h-4" />
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={() =>
                setTheme(currentTheme === "dark" ? "light" : "dark")
              }
              className="rounded-sm h-7 w-7"
            >
              {currentTheme === "dark" ? (
                <Sun className="w-3.5 h-3.5" />
              ) : (
                <Moon className="w-3.5 h-3.5" />
              )}
            </Button>
            {user ? (
              <div className="flex items-center gap-2 ml-auto">
                <Link
                  to="/subscription"
                  onClick={() => setMobileOpen(false)}
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  <CreditCard className="w-4 h-4" />
                </Link>
                <Link
                  to="/profile"
                  onClick={() => setMobileOpen(false)}
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  <UserCog className="w-4 h-4" />
                </Link>
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    onClick={() => setMobileOpen(false)}
                    className="text-sm text-brand-primary"
                  >
                    <Shield className="w-4 h-4" />
                  </Link>
                )}
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    handleLogout();
                  }}
                  className="text-sm text-red-400"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      </header>
      {/* Push Notification Prompt */}
      {showPushPrompt ? (
        <div
          className="fixed bottom-4 right-4 z-[60] w-80 max-w-[calc(100vw-2rem)] rounded-md border border-border bg-background shadow-lg p-4"
          data-testid="push-prompt"
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
              <ScanEye className="w-4 h-4 text-brand-primary" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">
                {t("notify.permission.title")}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t("notify.permission.desc")}
              </p>
              <div className="flex gap-2 mt-3">
                <Button
                  size="sm"
                  className="rounded-sm bg-brand-primary text-white text-xs h-7 px-3"
                  onClick={() => {
                    requestPermission();
                    setShowPushPrompt(false);
                    localStorage.setItem("veriksa_push_dismissed", "1");
                  }}
                  data-testid="push-allow-btn"
                >
                  {t("notify.permission.allow")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-sm text-xs h-7 px-3"
                  onClick={() => {
                    setShowPushPrompt(false);
                    localStorage.setItem("veriksa_push_dismissed", "1");
                  }}
                  data-testid="push-later-btn"
                >
                  {t("notify.permission.later")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

